/**
 * The reading surface.
 *
 * Two problems shape this file.
 *
 * 1. The chapter text is one string, and the sentences are offsets into it. The
 *    text BETWEEN two sentences is not decoration: it holds the paragraph breaks.
 *    So the layout below emits every character exactly once, either inside a
 *    sentence or inside a gap, and cuts the result into paragraphs on blank lines.
 *    A sentence that straddles a paragraph break simply appears twice, with the
 *    same index, and highlights in both halves.
 * 2. The highlight moves sixty times a second. So the paragraph list is computed
 *    once per chapter, every sentence is a memoised component, and the only prop
 *    that changes on a frame is `active` on the two sentences concerned.
 */

import { memo, useCallback, useEffect, useMemo, useRef } from "react";

import type { Sentence } from "../lib/types";
import { useSwipe } from "../lib/useSwipe";

/** How long auto-scroll stays out of the way after the reader scrolls by hand. */
const MANUAL_PAUSE = 6000;
/** Smooth scrolling keeps firing scroll events. Ignore them for this long. */
const PROGRAMMATIC_WINDOW = 1200;

type Piece =
  | { kind: "gap"; key: string; text: string }
  | { kind: "sentence"; key: string; text: string; index: number };

interface Paragraph {
  key: string;
  pieces: Piece[];
}

/** Cut `text` into paragraphs on blank lines, keeping every character. */
export function layout(text: string, sentences: Sentence[]): Paragraph[] {
  const blocks: [number, number][] = [];
  const breaks = /\n[ \t]*\n\s*/g;
  let cursor = 0;
  for (let match = breaks.exec(text); match; match = breaks.exec(text)) {
    blocks.push([cursor, match.index]);
    cursor = match.index + match[0].length;
  }
  blocks.push([cursor, text.length]);

  const paragraphs: Paragraph[] = [];
  let first = 0; // sentences are ordered, so the scan never restarts from zero

  for (const [start, end] of blocks) {
    if (end <= start || !text.slice(start, end).trim()) continue;
    const pieces: Piece[] = [];
    let at = start;

    while (first < sentences.length && sentences[first].ce <= start) first += 1;
    for (let s = first; s < sentences.length && sentences[s].cs < end; s += 1) {
      const sentence = sentences[s];
      const from = Math.max(sentence.cs, start);
      const to = Math.min(sentence.ce, end);
      if (to <= from) continue;
      if (from > at)
        pieces.push({ kind: "gap", key: `g${at}`, text: text.slice(at, from) });
      pieces.push({
        kind: "sentence",
        key: `s${sentence.i}-${from}`,
        text: text.slice(from, to),
        index: sentence.i,
      });
      at = to;
    }
    if (at < end)
      pieces.push({ kind: "gap", key: `g${at}`, text: text.slice(at, end) });
    if (pieces.length) paragraphs.push({ key: `p${start}`, pieces });
  }
  return paragraphs;
}

const SentenceSpan = memo(function SentenceSpan({
  text,
  index,
  active,
  interactive,
  onSelect,
}: {
  text: string;
  index: number;
  active: boolean;
  interactive: boolean;
  onSelect: (index: number) => void;
}) {
  const base =
    "box-decoration-clone rounded-fib3 transition-colors duration-300";
  const state = active
    ? "bg-[var(--color-spoken)] shadow-[inset_3px_0_0_var(--color-spoken-edge)]"
    : interactive
      ? "hover:bg-[var(--color-accent-soft)] cursor-pointer"
      : "";
  return (
    <span
      data-s={index}
      // The keyboard path to a sentence is Up and Down, not fifty tab stops. Only
      // the spoken one is reachable by tab, as an anchor back into the text.
      tabIndex={active ? 0 : -1}
      role={interactive ? "button" : undefined}
      aria-current={active ? "true" : undefined}
      onClick={interactive ? () => onSelect(index) : undefined}
      onKeyDown={
        interactive
          ? (event) => {
              if (event.key === "Enter") onSelect(index);
            }
          : undefined
      }
      className={`${base} ${state}`}
    >
      {text}
    </span>
  );
});

const Para = memo(function Para({
  pieces,
  focus,
  interactive,
  onSelect,
}: {
  pieces: Piece[];
  focus: number;
  interactive: boolean;
  onSelect: (index: number) => void;
}) {
  return (
    <p className="mb-[0.9em] last:mb-0">
      {pieces.map((piece) =>
        piece.kind === "gap" ? (
          <span key={piece.key}>{piece.text}</span>
        ) : (
          <SentenceSpan
            key={piece.key}
            text={piece.text}
            index={piece.index}
            active={piece.index === focus}
            interactive={interactive}
            onSelect={onSelect}
          />
        ),
      )}
    </p>
  );
});

export function TextView({
  text,
  sentences,
  focus,
  playing,
  interactive,
  font,
  chapterIndex,
  onSelect,
  onTurnChapter,
}: {
  text: string;
  sentences: Sentence[];
  focus: number;
  playing: boolean;
  interactive: boolean;
  font: "serif" | "sans";
  chapterIndex: number;
  onSelect: (index: number) => void;
  onTurnChapter: (direction: 1 | -1) => void;
}) {
  const scroller = useRef<HTMLDivElement>(null);

  // A chapter is this view's page. Swiping sideways turns it, exactly as
  // swiping sideways turns a page in the scan view: one gesture, one meaning,
  // whichever half of the book the reader is looking at.
  const { handlers: swipe, justSwiped } = useSwipe(onTurnChapter);
  const suppressUntil = useRef(0);
  const programmaticUntil = useRef(0);

  const focusRef = useRef(focus);
  focusRef.current = focus;

  // A swipe ends in a click on Android: without this, turning the chapter also
  // starts reading whatever line the finger lifted off.
  const tap = useCallback(
    (index: number) => {
      if (justSwiped()) return;
      onSelect(index);
    },
    [justSwiped, onSelect],
  );

  const paragraphs = useMemo(() => layout(text, sentences), [text, sentences]);

  const scrollTo = useCallback((target: Element, smooth: boolean) => {
    const box = scroller.current;
    if (!box) return;
    const area = box.getBoundingClientRect();
    const rect = target.getBoundingClientRect();
    const top = rect.top - area.top + box.scrollTop;
    // Middle third. Inside it, leave the page alone: a highlight that never stops
    // creeping is worse than one that jumps every few sentences.
    const upper = box.scrollTop + box.clientHeight * 0.3;
    const lower = box.scrollTop + box.clientHeight * 0.66;
    if (top >= upper && top + rect.height <= lower) return;
    programmaticUntil.current = performance.now() + PROGRAMMATIC_WINDOW;
    box.scrollTo({
      top: Math.max(0, top - box.clientHeight * 0.36),
      behavior: smooth ? "smooth" : "auto",
    });
  }, []);

  // A scroll the reader did themselves buys them a few seconds of quiet. Someone
  // who scrolls back to re-read a line must not be dragged forward again.
  useEffect(() => {
    const box = scroller.current;
    if (!box) return;
    const onScroll = () => {
      const now = performance.now();
      if (now > programmaticUntil.current)
        suppressUntil.current = now + MANUAL_PAUSE;
    };
    box.addEventListener("scroll", onScroll, { passive: true });
    return () => box.removeEventListener("scroll", onScroll);
  }, []);

  // On a chapter change, land on the reading position at once, without animation.
  useEffect(() => {
    const box = scroller.current;
    if (!box) return;
    const target = box.querySelector(`[data-s="${focusRef.current}"]`);
    if (target) scrollTo(target, false);
    else box.scrollTo({ top: 0 });
    // Chapter turns only. The position is read through a ref so that this does not
    // re-run on every highlight, which would fight the smooth scroll below.
  }, [chapterIndex, paragraphs, scrollTo]);

  useEffect(() => {
    if (!playing) return;
    if (performance.now() < suppressUntil.current) return;
    const box = scroller.current;
    const target = box?.querySelector(`[data-s="${focus}"]`);
    if (!target) return;
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    scrollTo(target, !still);
  }, [focus, playing, scrollTo]);

  return (
    <div
      ref={scroller}
      {...swipe}
      className="h-full touch-pan-y overflow-y-auto overscroll-contain"
    >
      <article
        className="reading mx-auto px-6 pt-[14vh] pb-[50vh] text-[var(--color-ink)]"
        data-font={font}
      >
        {paragraphs.map((paragraph) => (
          <Para
            key={paragraph.key}
            pieces={paragraph.pieces}
            focus={focus}
            interactive={interactive}
            onSelect={tap}
          />
        ))}
      </article>
    </div>
  );
}
